/**
 * `useTeamDateRequests` / `useCreateTeamDateRequest` /
 * `useDeleteTeamDateRequest` — TanStack hooks for the lead-side
 * team date-requests routes (#686 WS1 origin; date-based read #730).
 *
 * Backed by the routes in `backend/src/api/routes/date_requests.py`:
 *
 *   * `GET    /api/teams/{team_id}/date-requests`           — list every
 *     member's date-request (leave + call-block) across the team. An
 *     optional `{ start, end }` date range (issue #730) switches to the
 *     *date-range* view: each request's window is clipped to `[start,
 *     end]` and non-overlapping requests drop out. This is the canonical
 *     view the Team-page editor renders — leaves are date-based, not
 *     cycle-tied, so the lead scrolls a calendar window (a ~two-month
 *     default, navigable by month) independent of any cycle.
 *   * `POST   /api/teams/{team_id}/date-requests`           — lead
 *     creates a request on behalf of a member (leave-on-behalf, #403).
 *   * `DELETE /api/teams/{team_id}/date-requests/{id}`      — lead
 *     removes any member's request.
 *
 * Cache-key shape mirrors the team-data hooks (`["team-date-requests",
 * teamId, start, end, accountId]`). The range segment keeps each visible
 * window in its own cache entry so paging by month doesn't clobber the
 * previously-viewed range. Both mutations invalidate the whole
 * `["team-date-requests", teamId]` prefix so the table refreshes after
 * add / remove.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "../lib/AuthContext";
import { type ApiError, apiFetch } from "./client";
import { invalidateRosterReads } from "./rosterInvalidation";
import type { components } from "./schema.gen";

export type DateRequestRead = components["schemas"]["DateRequestRead"];
export type DateRequestListResponse = components["schemas"]["DateRequestListResponse"];
export type DateRequestCreateBody = components["schemas"]["DateRequestCreateBody"];
export type TierDateRequestSummary = components["schemas"]["TierDateRequestSummary"];

const STALE_TIME_MS = 30_000;

export const TEAM_DATE_REQUESTS_KEY = (teamId: string) => ["team-date-requests", teamId] as const;

/** An inclusive `[start, end]` calendar window (ISO `YYYY-MM-DD`). */
export interface DateRequestRange {
  start: string;
  end: string;
}

/**
 * List the team's date-requests over a date RANGE (issue #730). Pass a
 * `{ start, end }` inclusive window to fetch the date-range view — each
 * request clipped to the window, non-overlapping requests dropped, and
 * the per-tier rollup (#717) computed over the same window. Omit `range`
 * (or pass `null`/`undefined`) for the full roster-wide calendar windows.
 */
export function useTeamDateRequests(teamId: string | undefined, range?: DateRequestRange | null) {
  const { accountId } = useAuth();
  const query = range
    ? `?start=${encodeURIComponent(range.start)}&end=${encodeURIComponent(range.end)}`
    : "";
  return useQuery({
    queryKey: [
      ...TEAM_DATE_REQUESTS_KEY(teamId ?? ""),
      range?.start ?? null,
      range?.end ?? null,
      accountId ?? "",
    ],
    enabled: Boolean(teamId && accountId),
    staleTime: STALE_TIME_MS,
    retry: false,
    queryFn: () => apiFetch<DateRequestListResponse>(`/api/teams/${teamId}/date-requests${query}`),
  });
}

type CacheSnapshots = [readonly unknown[], DateRequestListResponse | undefined][];

export function useCreateTeamDateRequest(teamId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<
    DateRequestRead,
    ApiError | Error,
    DateRequestCreateBody,
    { snapshots: CacheSnapshots }
  >({
    mutationFn: (body) =>
      apiFetch<DateRequestRead>(`/api/teams/${teamId}/date-requests`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    // Optimistic: drop a provisional row into every cached window for this
    // team so a painted cell fills instantly — no waiting on the POST +
    // refetch round-trip. Reconciled by the onSettled invalidation.
    onMutate: async (body) => {
      const key = ["team-date-requests", teamId ?? ""];
      await queryClient.cancelQueries({ queryKey: key });
      const snapshots = queryClient.getQueriesData<DateRequestListResponse>({ queryKey: key });
      const provisional: DateRequestRead = {
        id: `optimistic-${crypto.randomUUID()}`,
        team_id: teamId ?? "",
        membership_id: body.membership_id,
        cycle_id: null,
        start_date: body.start_date,
        end_date: body.end_date,
        window_start: body.start_date,
        window_end: body.end_date,
        request_type: body.request_type,
        category_key: body.category_key ?? null,
        source: "lead",
        submitted_at: new Date().toISOString(),
        // #1667 — a fresh request is an indication; the refetch reconciles.
        status: "indicated",
        urgent: false,
      };
      queryClient.setQueriesData<DateRequestListResponse>({ queryKey: key }, (old) =>
        old ? { ...old, requests: [...old.requests, provisional] } : old,
      );
      return { snapshots };
    },
    onError: (_e, _v, ctx) => {
      for (const [k, data] of ctx?.snapshots ?? []) {
        queryClient.setQueryData<DateRequestListResponse>(k, data);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["team-date-requests", teamId ?? ""] });
      // #2277 — the daily leave-quota lines read the same rows.
      queryClient.invalidateQueries({ queryKey: ["leave-quota", teamId ?? ""] });
      invalidateRosterConsequences(queryClient);
    },
  });
}

/**
 * Backlog #91 — writing or REMOVING a date request changes what the
 * published roster says: the seat's strike, the day's shortfall, the
 * "Review cover" chip, the "Coverage · N short" chip. These hooks are
 * team-scoped and hold no cycle id, so every cached cycle's copy of those
 * four reads is invalidated by prefix. Cheap: only mounted queries refetch,
 * and the lead is looking at one cycle.
 */
function invalidateRosterConsequences(queryClient: ReturnType<typeof useQueryClient>): void {
  void invalidateRosterReads(queryClient);
  queryClient.invalidateQueries({ queryKey: ["cycle-shortfall-options"] });
  queryClient.invalidateQueries({ queryKey: ["cycle-coverage-review"] });
  queryClient.invalidateQueries({ queryKey: ["cycle-allocation-gaps"] });
  queryClient.invalidateQueries({ queryKey: ["mc-attention"] });
}

export function useDeleteTeamDateRequest(teamId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError | Error, string, { snapshots: CacheSnapshots }>({
    mutationFn: (requestId) =>
      apiFetch<void>(`/api/teams/${teamId}/date-requests/${requestId}`, {
        method: "DELETE",
      }),
    // Optimistic: remove the row from every cached window immediately so an
    // erased cell clears with no lag; reconciled by onSettled.
    onMutate: async (requestId) => {
      const key = ["team-date-requests", teamId ?? ""];
      await queryClient.cancelQueries({ queryKey: key });
      const snapshots = queryClient.getQueriesData<DateRequestListResponse>({ queryKey: key });
      queryClient.setQueriesData<DateRequestListResponse>({ queryKey: key }, (old) =>
        old ? { ...old, requests: old.requests.filter((r) => r.id !== requestId) } : old,
      );
      return { snapshots };
    },
    onError: (_e, _v, ctx) => {
      for (const [k, data] of ctx?.snapshots ?? []) {
        queryClient.setQueryData<DateRequestListResponse>(k, data);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["team-date-requests", teamId ?? ""] });
      // #2277 — a removed leave leaves the daily leave-quota count.
      queryClient.invalidateQueries({ queryKey: ["leave-quota", teamId ?? ""] });
      // The lead-side withdraw: the row is gone, so the seat is no longer
      // off sick and the day is no longer short. Same fan-out as the write.
      invalidateRosterConsequences(queryClient);
      // #2969 — a Remove with the switch on starts or ends a held leave
      // change: the roster page's list of them reads again.
      queryClient.invalidateQueries({ queryKey: ["leave-change-resolves", teamId ?? ""] });
    },
  });
}
